import React, { useMemo, useState } from "react";
import "./HeaderNav.scss";
import HeaderNavMobile from "./HeaderNavMobile";
import HeaderNavDesktop from "./HeaderNavDesktop";
import {
	TheAepsyWayLink,
	SelfEmploymentLink,
} from "@components/CMSComponents/links";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import ChapterSubLinks from "./ChapterSubLinks";
import { useLocale } from "../../hooks";
import Section from "@atoms/Section/Section";
import { UserLang } from "@constants/common";

interface Props {
	theme: "white" | "dark";
	location?: string;
	signUpAsProviderShown?: boolean;
}

export type SubMenu = {
	title: string;
	list: Array<{
		title?: string;
		url?: string;
		customLink?: any;
		isLink?: boolean;
	}>;
};

const SECOND_SUB_MENU: SubMenu[] = [
	{
		title: "nav.label.about",
		list: [
			{
				title: "nav.label.about.us",
				url: "/about/",
			},
			{
				title: "nav.label.approach",
				url: "/approach/",
			},
			{
				title: "nav.label.coverage",
				url: "/coverage/",
			},
			{
				title: "nav.label.jobs",
				url: "/jobs/",
			},
			{
				title: "nav.label.stories",
				url: "/inspiration/",
			},
			{
				title: "nav.label.faq",
				url: "/faq/",
			},
		],
	},
	{
		title: "nav.label.join.as.psychologist",
		list: [
			{
				title: "nav.label.for.psychologist",
				url: "/for-therapists/",
			},
			{
				customLink: (
					<Section spacingBottom="xxs">
						<TheAepsyWayLink
							variant="inline"
							linkProps={{
								className: "HeaderNav--item--link",
								activeClassName: "is-active",
							}}
						/>
					</Section>
				),
			},
			{
				customLink: (
					<Section spacingBottom="xxs">
						<SelfEmploymentLink
							linkProps={{
								className: "HeaderNav--item--link",
								activeClassName: "is-active",
							}}
						/>
					</Section>
				),
			},
		],
	},
];

const THRESHOLD = 20;

const HeaderNav: React.FC<Props> = ({
	theme = "dark",
	location,
	signUpAsProviderShown,
}) => {
	const [headerIsFixed, setHeaderIsFixed] = useState(false);
	const { currentLanguage } = useLocale();

	const FIRST_SUB_MENU: SubMenu[] = useMemo(() => {
		return [
			...(currentLanguage === UserLang.German
				? [
						{
							title: "nav.label.by.challenges",
							list: [
								{
									title: "Selbstwert",
									url: "/ressourcen/selbstwert/",
								},
								{
									title: "Stress",
									url: "/ressourcen/stress/",
								},
								{
									title: "Depression",
									url: "/ressourcen/depression/",
								},
								{
									title: "Angst",
									url: "/ressourcen/angst/",
								},
								{
									title: "generic.view.more",
									url: "/ressourcen/",
									isLink: true,
								},
							],
						},
					]
				: []),
			{
				title: "nav.label.by.methods",
				list: [
					{
						customLink: <ChapterSubLinks />,
					},
				],
			},
		];
	}, [currentLanguage]);

	useScrollPosition(
		({ prevPos, currPos }) => {
			if (Math.abs(currPos.y) > THRESHOLD) {
				setHeaderIsFixed(true);
			} else {
				setHeaderIsFixed(false);
			}
		},
		[headerIsFixed],
		undefined,
		false,
		30,
	);

	return (
		<>
			<HeaderNavDesktop
				theme={theme}
				signUpAsProviderShown={signUpAsProviderShown}
				firstSubMenu={FIRST_SUB_MENU}
				secondSubMenu={SECOND_SUB_MENU}
				headerIsFixed={headerIsFixed}
			/>
			<HeaderNavMobile
				headerIsFixed={headerIsFixed}
				theme={theme}
				location={location}
				list={[...FIRST_SUB_MENU, ...SECOND_SUB_MENU]}
			/>
		</>
	);
};

export default HeaderNav;
